// src/features/live/FeaturedLive.tsx — the featured-live player block: player
// area (GeneratedThumb art, LIVE badge, viewer count, disabled chrome), creator
// + working Follow, title, description, tags. Shared by LivePage (the current
// featured channel) and LiveDetailPage (whichever channel was clicked) — "same
// placeholder treatment" per slice-4-xl.
import styled from 'styled-components';
import { GeneratedThumb } from '@/components/GeneratedThumb/GeneratedThumb';
import { ThumbBadge } from '@/components/GeneratedThumb/ThumbBadge';
import { PlayGlyph } from '@/components/GeneratedThumb/PlayGlyph';
import { Avatar, Button, DisabledHint } from '@/ui';
import { Icon } from '@/icons';
import { useFollow, useRequireAuth, followButtonLabel } from '@/hooks';
import { formatViewers, LIVE_BADGE_BG } from './meta';
import type { LiveChannel, LiveCreator } from '@/core/models/live';

const Wrap = styled.div``;

// Same structural note as VideoDetailPage's PlayerWrap: DisabledHint wraps only
// the individual chrome glyphs nested inside this sized, positioned frame — an
// inline-flex wrapper whose only content is absolutely positioned collapses to
// 0×0 (see ui/DisabledHint's sizing note).
const PlayerWrap = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  aspect-ratio: 16 / 9;
  margin-bottom: 14px;
`;

const PlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
`;

const LiveTag = styled(ThumbBadge)`
  background: ${LIVE_BADGE_BG};
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 700;
`;

const ChromeBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.6) 0%, transparent 100%);
`;

const ChromeButton = styled.button`
  display: inline-grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  cursor: not-allowed;

  /* Greyed, per the owner decision, full-strength white read as enabled
     chrome, not disabled (slice-4-xl fix round S1). aria-disabled is always
     'true' here (see DisabledHint's contract), but the selector documents the
     intent explicitly rather than relying on the inline color alone. */
  &[aria-disabled='true'] {
    color: rgba(255, 255, 255, 0.45);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 2px;
  }
`;

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const AuthorText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const MetaLine = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Description = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: 1.6;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;

const Tag = styled.span`
  padding: 3px 9px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: 11px;
  color: ${({ theme }) => theme.color.textMuted};
`;

const CHROME_CONTROLS: { icon: 'pause' | 'volume-2' | 'settings' | 'maximize'; label: string }[] = [
  { icon: 'pause', label: 'Pause' },
  { icon: 'volume-2', label: 'Volume' },
  { icon: 'settings', label: 'Settings' },
  { icon: 'maximize', label: 'Fullscreen' },
];

export interface FeaturedLiveProps {
  channel: LiveChannel;
  creator?: LiveCreator;
  /**
   * The channel title's heading level. LivePage renders this block alongside
   * its own page `<h1>Live</h1>`, so the title here must be an `<h2>`; when
   * LiveDetailPage renders it as the whole page's title, it must be the page's
   * only `<h1>` (slice-4-xl fix round S2 — a prior version hardcoded `<h1>`
   * in both places, so /live shipped with two h1s). Mirrors Videos' precedent
   * (hero h2, detail page h1).
   */
  headingLevel?: 'h1' | 'h2';
}

export function FeaturedLive({ channel, creator, headingLevel = 'h2' }: FeaturedLiveProps) {
  const { isFollowing, isRequested, toggleFollow } = useFollow(creator?.handle ?? '');
  const requireAuth = useRequireAuth();

  function handleFollow() {
    if (!requireAuth('follow')) return;
    void toggleFollow();
  }

  return (
    <Wrap>
      <PlayerWrap>
        <GeneratedThumb seedKey={channel.id} fill live />
        <PlayOverlay>
          <DisabledHint explanation="Playback isn't available yet. This is a placeholder.">
            {(describedBy) => <PlayGlyph describedBy={describedBy} size={56} tone="overlay" />}
          </DisabledHint>
        </PlayOverlay>
        <LiveTag $corner="tl">Live</LiveTag>
        <ThumbBadge $corner="tr">{formatViewers(channel.viewerCount)}</ThumbBadge>
        <ChromeBar>
          {CHROME_CONTROLS.map((c) => (
            <DisabledHint key={c.icon} explanation={`${c.label} isn't available yet. This is a placeholder.`}>
              {(describedBy) => (
                <ChromeButton
                  type="button"
                  aria-disabled="true"
                  aria-describedby={describedBy}
                  aria-label={`${c.label} (unavailable)`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <Icon name={c.icon} size={16} />
                </ChromeButton>
              )}
            </DisabledHint>
          ))}
        </ChromeBar>
      </PlayerWrap>

      {creator && (
        <AuthorRow>
          <Avatar name={creator.name} avatarKey={creator.av} size={40} />
          <AuthorText>
            <AuthorName>{creator.name}</AuthorName>
            <MetaLine>{creator.handle}</MetaLine>
          </AuthorText>
          <Button
            variant={isFollowing || isRequested ? 'secondary' : 'outline'}
            size="sm"
            onClick={handleFollow}
            aria-label={`${followButtonLabel(isFollowing, isRequested)} ${creator.handle}`}
          >
            {followButtonLabel(isFollowing, isRequested)}
          </Button>
        </AuthorRow>
      )}

      <Title as={headingLevel}>{channel.title}</Title>
      <Description>{channel.description}</Description>
      <Tags>
        {channel.tags.map((tag) => (
          <Tag key={tag}>{tag}</Tag>
        ))}
      </Tags>
    </Wrap>
  );
}
